import * as React from 'react';
import { styled } from '@mui/material/styles';
import Divider, { dividerClasses } from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import { paperClasses } from '@mui/material/Paper';
import { listClasses } from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import MenuButton from './MenuButton';
import { useNavigate } from 'react-router-dom'
import { abortActiveOfflineDownloads, syncOfflineServiceWorker } from '../../services/offlineService';
import { useUpload } from '../context/UploadContext';
import { useAuth } from '../context/AuthContext';

const MenuItem = styled(MuiMenuItem)({
  margin: '2px 0',
});

/** @param {{ setPage?: (value: string) => void, trigger?: (props: { onClick: (event: React.MouseEvent<HTMLElement>) => void, open: boolean }) => React.ReactNode }} props */
export default function OptionsMenu(props) {
  const { setPage, trigger } = props
  const [anchorEl, setAnchorEl] = React.useState(/** @type {HTMLElement | null} */ (null));
  const [isSignOutConfirmOpen, setIsSignOutConfirmOpen] = React.useState(false);
  const open = Boolean(anchorEl);
  const { isUploading, cancelUpload } = useUpload();
  const { user, signOut } = useAuth();

  const navigate = useNavigate();

  /** @param {React.MouseEvent<HTMLElement>} event */
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const performLogout = async () => {
    const uid = user?.uid;
    const [offlineResult, uploadResult] = await Promise.allSettled([
      abortActiveOfflineDownloads(),
      cancelUpload(uid),
    ]);
    if (offlineResult.status === 'rejected') {
      console.warn('Failed to abort active offline downloads on logout:', offlineResult.reason);
    }
    if (uploadResult.status === 'rejected') {
      console.warn('Failed to cancel active upload on logout:', uploadResult.reason);
    }

    try {
      await signOut();
    } catch (error) {
      // ignore sign-out error and still navigate
    }

    try {
      await syncOfflineServiceWorker(false);
    } catch (err) {
      console.warn('Failed to unregister offline service worker on logout:', err);
    }

    navigate('/');
  }

  const handleLogout = () => {
    if (isUploading) {
      setIsSignOutConfirmOpen(true);
      return;
    }
    performLogout();
  }

  const handleCancelSignOut = () => {
    setIsSignOutConfirmOpen(false);
  }

  const handleConfirmSignOut = () => {
    setIsSignOutConfirmOpen(false);
    performLogout();
  }

  const handleProfile = () => {
    if (typeof setPage === 'function') {
      setPage('Profile');
    } else {
      navigate('/dashboard', { state: { page: 'Profile' } });
    }
  }

  return (
    <React.Fragment>
      {trigger ? trigger({ onClick: handleClick, open }) : (
        <MenuButton
          aria-label="Open menu"
          onClick={handleClick}
          sx={{ borderColor: 'transparent' }}
        >
          <MoreVertRoundedIcon />
        </MenuButton>
      )}
      <Menu
        anchorEl={anchorEl}
        id="menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        disableScrollLock
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          [`& .${listClasses.root}`]: {
            padding: '4px',
          },
          [`& .${paperClasses.root}`]: {
            padding: 0,
          },
          [`& .${dividerClasses.root}`]: {
            margin: '4px -4px',
          },
        }}
      >
        {<MenuItem onClick={handleProfile}>Profile</MenuItem>
        /* <MenuItem onClick={handleClose}>My account</MenuItem>
        <Divider />
        <MenuItem onClick={handleClose}>Add another account</MenuItem>
        <MenuItem onClick={handleClose}>Settings</MenuItem>
        <Divider /> */}
        <MenuItem
          onClick={handleLogout}
          sx={{
            [`& .${listItemIconClasses.root}`]: {
              ml: 'auto',
              minWidth: 0,
            },
          }}
        >
          <ListItemText sx={{ width: 120}} >Logout</ListItemText>
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      </Menu>
      <Dialog open={isSignOutConfirmOpen} onClose={handleCancelSignOut} disableScrollLock>
        <DialogTitle>Upload in progress</DialogTitle>
        <DialogContent>
          <DialogContentText>
            An upload is still in progress. Signing out now will interrupt it. Are you sure you want to sign out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSignOut}>Cancel</Button>
          <Button onClick={handleConfirmSignOut} color="error" variant="contained">Sign out</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
